import React, { useEffect, useState } from 'react';
import { streamsService, LearningStream } from '../services/streams';
import { logsService, DailyLog } from '../services/logs';
import { resourcesService, Resource, ResourceCreate } from '../services/resources';
import { milestonesService, Milestone, MilestoneUpdate } from '../services/milestones';

type Track = 'ML' | 'CV' | 'QC' | 'QML' | 'Security' | 'Backend';

interface TrackInfo {
  name: string;
  timeline: string;
  description: string;
  resources: string[];
  milestones: string[];
}

const TechnicalSkills: React.FC = () => {
  const [mlStream, setMlStream] = useState<LearningStream | null>(null);
  const [selectedTrack, setSelectedTrack] = useState<Track>('ML');
  const [logs, setLogs] = useState<DailyLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [resources, setResources] = useState<Resource[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [showResourceForm, setShowResourceForm] = useState(false);
  const [newResource, setNewResource] = useState({ name: '', url: '', description: '' });

  const tracks: Record<Track, TrackInfo> = {
    ML: {
      name: 'Machine Learning & AI',
      timeline: 'Dec 2025 - Mar 2026 (4 months)',
      description: 'Core ML fundamentals including Linear Algebra, Calculus, and Supervised Learning',
      resources: [
        '3Blue1Brown: Essence of Linear Algebra (12 videos)',
        'MIT OCW: Calculus',
        'Fast.ai Course',
        'Kaggle Competitions',
      ],
      milestones: [
        'Complete Linear Algebra course (12 videos)',
        'Complete 10 problem sets',
        'Finish Supervised Learning track',
        'Build custom CNN from scratch',
      ],
    },
    CV: {
      name: 'Computer Vision',
      timeline: 'Apr 2026 - Jun 2026 (3 months)',
      description: 'Object detection, segmentation, and modern vision transformers',
      resources: [
        'YOLO v10 paper & implementation',
        'U-Net for segmentation',
        'Vision Transformer papers',
        'OpenCV tutorials',
      ],
      milestones: [
        'Implement object detection pipeline',
        'Complete segmentation project',
        'Read 5 recent CV papers',
        'Build Vision Transformer classifier',
      ],
    },
    QC: {
      name: 'Quantum Computing',
      timeline: 'Jul 2026 - Oct 2026 (4 months)',
      description: 'Quantum computing fundamentals, gates, circuits, and algorithms',
      resources: [
        'Qiskit Textbook',
        'IBM Quantum Experience',
        'Cirq documentation',
        'Quantum Algorithm papers',
      ],
      milestones: [
        'Understand qubits and superposition',
        'Implement Grover\'s algorithm',
        'Optimize quantum circuits',
        'Complete 3 QC projects',
      ],
    },
    QML: {
      name: 'Quantum Machine Learning',
      timeline: 'Nov 2026 - Jan 2027 (3 months)',
      description: 'Hybrid quantum-classical algorithms and VQE/QAOA',
      resources: [
        'PennyLane tutorials',
        'QML survey papers',
        'VQE implementation guides',
        'Hybrid algorithm research',
      ],
      milestones: [
        'Implement VQE algorithm',
        'Build quantum classifier',
        'Compare quantum vs classical',
        'Complete 2 QML projects',
      ],
    },
    Security: {
      name: 'Quantum Security',
      timeline: 'Feb 2027 - Mar 2027 (2 months)',
      description: 'Post-quantum cryptography and quantum key distribution',
      resources: [
        'NIST post-quantum standards',
        'CRYSTALS-Kyber documentation',
        'QKD research papers',
        'Quantum-safe algorithms',
      ],
      milestones: [
        'Implement post-quantum crypto',
        'Study QKD protocols',
        'Build security library',
        'Complete 1 security project',
      ],
    },
    Backend: {
      name: 'Backend Maintenance',
      timeline: 'Continuous (30 min - 1 hour daily)',
      description: 'Maintain Go/Python skills, Docker/K8s, and cloud infrastructure',
      resources: [
        'Go weekly challenges',
        'Kubernetes updates',
        'AWS/GCP quantum platforms',
        'Docker best practices',
      ],
      milestones: [
        'Weekly Go challenge',
        'K8s updates review',
        'Cloud quantum platform exploration',
        'Backend optimization projects',
      ],
    },
  };

  useEffect(() => {
    loadMLStream();
  }, []);

  const loadMLStream = async () => {
    try {
      const streams = await streamsService.getAll();
      const ml = streams.find(s => s.name === 'ML_AI');
      if (ml) {
        setMlStream(ml);
        const [mlLogs, streamResources, streamMilestones] = await Promise.all([
          logsService.getAll(ml.id),
          resourcesService.getAll(ml.id),
          milestonesService.getAll(),
        ]);
        setLogs(mlLogs);
        setResources(streamResources);
        setMilestones(streamMilestones);
      }
    } catch (error) {
      console.error('Error loading ML stream:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddResource = async () => {
    if (!mlStream || !newResource.name) return;
    
    try {
      const resourceData: ResourceCreate = {
        stream_id: mlStream.id,
        ...newResource,
      };
      await resourcesService.create(resourceData);
      
      setNewResource({ name: '', url: '', description: '' });
      setShowResourceForm(false);
      loadMLStream();
    } catch (error) {
      console.error('Error adding resource:', error);
      alert('Failed to add resource. Please try again.');
    }
  };

  const handleToggleMilestone = async (milestoneId: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'Complete' ? 'Pending' : 'Complete';
      const update: MilestoneUpdate = { status: newStatus as any };
      await milestonesService.update(milestoneId, update);
      loadMLStream();
    } catch (error) {
      console.error('Error updating milestone:', error);
      alert('Failed to update milestone. Please try again.');
    }
  };

  const getProgress = () => {
    if (!mlStream) return 0;
    return Math.round((mlStream.completed_hours / mlStream.target_hours) * 100);
  };

  const getWeeklyHours = () => {
    const last7Days = logs.filter(log => {
      const logDate = new Date(log.date);
      const daysAgo = (new Date().getTime() - logDate.getTime()) / (1000 * 60 * 60 * 24);
      return daysAgo <= 7;
    });
    return last7Days.reduce((sum, log) => sum + log.hours_studied, 0);
  };

  if (loading) {
    return <div className="p-6">Loading technical skills tracker...</div>;
  }

  const currentTrack = tracks[selectedTrack];
  const progress = getProgress();
  const weeklyHours = getWeeklyHours();

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Technical Skills Development
          </h1>
          <p className="text-gray-600">
            Master AI + Quantum Computing fundamentals, maintain backend expertise
          </p>
        </div>

        {/* Track Selector */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Learning Track</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {Object.entries(tracks).map(([key, track]) => (
              <button
                key={key}
                onClick={() => setSelectedTrack(key as Track)}
                className={`p-3 rounded-lg border-2 transition-colors ${
                  selectedTrack === key
                    ? 'border-primary bg-primary text-white'
                    : 'border-gray-300 hover:border-primary'
                }`}
              >
                <div className="font-semibold text-sm">{track.name}</div>
                <div className="text-xs mt-1 opacity-75">{track.timeline}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Current Track Progress */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-2xl font-semibold">{currentTrack.name}</h2>
              <p className="text-gray-600">{currentTrack.timeline}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Overall Progress</p>
              <p className="text-2xl font-bold text-primary">{progress}%</p>
            </div>
          </div>

          <p className="text-gray-700 mb-4">{currentTrack.description}</p>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-4 mb-6">
            <div
              className="bg-secondary h-4 rounded-full transition-all duration-300"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Total Hours</p>
              <p className="text-2xl font-bold">
                {mlStream?.completed_hours.toFixed(1) || 0} / {mlStream?.target_hours || 0}
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">This Week</p>
              <p className="text-2xl font-bold">{weeklyHours.toFixed(1)} hours</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Current Track</p>
              <p className="text-2xl font-bold">{selectedTrack}</p>
            </div>
          </div>
        </div>

        {/* Resources and Milestones */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Resources */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold mb-4">Resources</h3>
            <ul className="space-y-2 mb-4">
              {resources.length > 0 ? (
                resources.map((resource) => (
                  <li key={resource.id} className="flex items-start justify-between">
                    <div className="flex items-start flex-1">
                      <span className="text-primary mr-2">•</span>
                      <div>
                        {resource.url ? (
                          <a
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {resource.name}
                          </a>
                        ) : (
                          <span className="text-gray-700">{resource.name}</span>
                        )}
                        {resource.description && (
                          <p className="text-xs text-gray-500 mt-1">{resource.description}</p>
                        )}
                      </div>
                    </div>
                  </li>
                ))
              ) : (
                <li className="text-gray-500 italic">No resources added yet. Start by adding your first resource!</li>
              )}
            </ul>
            <button
              onClick={() => setShowResourceForm(!showResourceForm)}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              {showResourceForm ? 'Cancel' : 'Add Resource'}
            </button>

            {showResourceForm && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold mb-3">Add New Resource</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Resource Name *</label>
                    <input
                      type="text"
                      value={newResource.name}
                      onChange={(e) => setNewResource({ ...newResource, name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="e.g., 3Blue1Brown Linear Algebra"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">URL (optional)</label>
                    <input
                      type="url"
                      value={newResource.url}
                      onChange={(e) => setNewResource({ ...newResource, url: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Description (optional)</label>
                    <input
                      type="text"
                      value={newResource.description}
                      onChange={(e) => setNewResource({ ...newResource, description: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Brief description"
                    />
                  </div>
                  <button
                    onClick={handleAddResource}
                    className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
                  >
                    Save Resource
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Milestones */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold mb-4">Milestones</h3>
            <ul className="space-y-3 mb-4">
              {milestones.length > 0 ? (
                milestones.map((milestone) => (
                  <li key={milestone.id} className="flex items-start">
                    <input
                      type="checkbox"
                      className="mt-1 mr-3 w-5 h-5 cursor-pointer"
                      checked={milestone.status === 'Complete'}
                      onChange={() => handleToggleMilestone(milestone.id, milestone.status)}
                    />
                    <div className="flex-1">
                      <span className={`text-gray-700 ${milestone.status === 'Complete' ? 'line-through' : ''}`}>
                        {milestone.name}
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        Due: {new Date(milestone.due_date).toLocaleDateString()} | 
                        Status: {milestone.status}
                      </p>
                    </div>
                  </li>
                ))
              ) : (
                <li className="text-gray-500 italic">No milestones added yet. Start by adding your first milestone!</li>
              )}
            </ul>
            <p className="text-xs text-gray-500 mb-4">Note: Milestone creation requires phase setup in database</p>
          </div>
        </div>

        {/* Learning Path Visualization */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-4">Learning Path</h3>
          <div className="flex flex-wrap items-center gap-4">
            {Object.entries(tracks).map(([key, track], index) => (
              <React.Fragment key={key}>
                <div
                  className={`p-3 rounded-lg border-2 ${
                    selectedTrack === key
                      ? 'border-primary bg-primary text-white'
                      : index < Object.keys(tracks).indexOf(selectedTrack)
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300'
                  }`}
                >
                  <div className="font-semibold text-sm">{track.name}</div>
                </div>
                {index < Object.keys(tracks).length - 1 && (
                  <span className="text-gray-400">→</span>
                )}
              </React.Fragment>
            ))}
          </div>
          <p className="text-sm text-gray-600 mt-4">
            <span className="bg-green-50 border border-green-500 px-2 py-1 rounded mr-2">Completed</span>
            <span className="bg-primary text-white px-2 py-1 rounded mr-2">Current</span>
            <span className="bg-gray-100 border border-gray-300 px-2 py-1 rounded">Upcoming</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TechnicalSkills;

